import React, { useEffect, useRef, useState } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';
import helpers from '@gloat/helpers';

// components
import { Flex } from '../box';
import { IconAnimatedChevron } from '../icon';
import { ActionLink } from '../link';

export interface CollapseTextProps {
  className?: string;
  expandLabel?: string | React.ReactNode;
  shrinkLabel?: string | React.ReactNode;
  content: React.ReactNode;
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  lines?: number;
}

const CollapseTextComponent = ({
  className,
  expandLabel = 'Read more',
  shrinkLabel = 'Read less',
  content,
  onClick,
}: CollapseTextProps) => {
  const [allowExpanding, setAllowExpanding] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAllowExpanding(helpers.isOverflown(ref.current) || allowExpanding);
    // eslint-disable-next-line react-hooks/refs
  }, [content, ref.current]);

  if (!content) {
    return null;
  }

  return (
    <div data-testid={'collapse-text'} className={className}>
      <div ref={ref} className={classNames('content', { compact: !isExpanded })}>
        <div ref={contentRef} tabIndex={-1} style={{ outline: 'none' }}>
          {content}
        </div>
      </div>
      {allowExpanding && (
        <ActionLink
          decoration={'none'}
          data-testid={'toggle'}
          aria-expanded={isExpanded}
          onClick={e => {
            const willExpand = !isExpanded;
            setIsExpanded(willExpand);
            if (willExpand) {
              contentRef.current?.focus();
            }
            onClick?.(e);
          }}
        >
          <Flex alignItems={'center'}>
            {isExpanded ? shrinkLabel : expandLabel}
            <IconAnimatedChevron isOn={isExpanded} size={'xl'} />
          </Flex>
        </ActionLink>
      )}
    </div>
  );
};

const CollapseText = styled(CollapseTextComponent)<CollapseTextProps>`
  > .content {
    &.compact {
      display: -webkit-box;
      -webkit-line-clamp: ${props => props.lines || 2};
      -webkit-box-orient: vertical;
      overflow: hidden;
    }
  }
`;

export default CollapseText;
